import { useRef } from 'react';
import clsx from 'clsx';

import type { CheckboxButtonProps } from 'components/CheckboxButton';

import styles from './CheckboxButtonBar.module.css';

import { CheckboxButton } from 'components/CheckboxButton';
import { Label } from 'components/Label';
import { Icon } from 'components/Icon';

type CheckboxProps = {
  label: string;
  value: string;
  disabled?: boolean;
};

export interface CheckboxButtonBarProps {
  /** array with objects with label, value */
  checkboxes: CheckboxProps[];
  //** array with values */
  defaultValues?: string[];
  label?: string;
  /** 'top' | 'left' */
  labelPosition?: 'top' | 'left';
  required?: boolean;
  /** error text to display if set */
  notValidatedText?: string;
  /** disables all checkboxes in the bar */
  disableGroup?: boolean;
  onSetValues: (value: string[]) => void;
}

/** Represents a component that shows a collection of [CheckboxButton](/story/atoms-checkboxbutton--checkbox-button-example)s. */
export const CheckboxButtonBar = ({
  checkboxes,
  defaultValues,
  label,
  labelPosition = 'top',
  required = false,
  notValidatedText = '',
  disableGroup = false,
  onSetValues,
}: CheckboxButtonBarProps) => {
  const values = useRef<string[]>(defaultValues ? defaultValues.map(String) : []);

  const handleChange = (setValue: string | null, value: string) => {
    if (setValue === null) {
      values.current = values.current.filter((val) => val !== value);
    } else {
      values.current = [...values.current, value];
    }
    onSetValues(values.current);
  };

  const getVariant = (index: number, totalItems: number): CheckboxButtonProps['variant'] => {
    if (index === 0 && totalItems !== 1) {
      return 'start';
    } else if (index < totalItems - 1) {
      return 'middle';
    } else if (totalItems === 1) {
      return 'single';
    } else {
      return 'end';
    }
  };

  return (
    <div
      className={clsx(
        styles['checkbox-button-bar__container'],
        styles[`checkbox-button-bar__container--label-${labelPosition}`]
      )}
    >
      {label && (
        <Label htmlFor="" required={required}>
          {label}
        </Label>
      )}
      <div className={styles['checkbox-button-bar__row-container']}>
        <div className={styles['checkbox-button-bar__row']}>
          {checkboxes.map(({ label, value, disabled }, index) => (
            <CheckboxButton
              key={`${value}`}
              label={label}
              value={value}
              isChecked={defaultValues ? defaultValues.includes(value) : false}
              variant={getVariant(index, checkboxes.length)}
              disabled={disabled || disableGroup}
              notValid={notValidatedText !== ''}
              onSetValue={(newValue) => handleChange(newValue, value)}
            />
          ))}
        </div>
        {notValidatedText !== '' && (
          <div className={styles['checkbox-button-bar__error']}>
            <Icon name="ExclamationTriangleFill" size={24} />
            {notValidatedText}
          </div>
        )}
      </div>
    </div>
  );
};
